package app.spliddy.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import app.spliddy.web.AppLanguage.Companion.current
import app.spliddy.web.AppLanguage.Companion.currentState
import com.varabyte.kobweb.browser.storage.createStorageKey
import com.varabyte.kobweb.browser.storage.getItem
import com.varabyte.kobweb.browser.storage.setItem
import io.github.skeptick.libres.LibresLocale
import io.github.skeptick.libres.LibresSettings
import io.github.skeptick.libres.getPlatformDefaultLibresLocale
import kotlinx.browser.window

private val rootAppLanguageState by lazy { mutableStateOf(AppLanguage.ENGLISH) }

// Set by [AppLanguage#provide]
private val LocalAppLanguage = compositionLocalOf { rootAppLanguageState }

enum class AppLanguage(
    val libresLocale: LibresLocale,
    val icon: String,
) {
    GERMAN(LibresLocale("de", null, null), "🇩🇪"),
    ENGLISH(LibresLocale("en", null, null), "🇬🇧"),
    ;

    companion object {
        /**
         * The current app language, exposed as a [MutableState] so that you can change it and have the UI update.
         *
         * See also [current] if you only need read-only access to the current app language.
         */
        val currentState: MutableState<AppLanguage> @Composable get() = LocalAppLanguage.current

        /**
         * The current app language.
         *
         * By default, this will be a global app language that affects the whole site. However, if you check this value
         * within a `Surface` that sets the `AppLanguageOverride` parameter, it will fetch the local app language instead.
         *
         * See also [currentState] if you need to modify the current language.
         */
        val current: AppLanguage
            @Composable @ReadOnlyComposable
            get() = LocalAppLanguage.current.value
    }

    val isGerman get() = (this == GERMAN)
    val isEnglish get() = (this == ENGLISH)
    val opposite
        get() =
            when (this) {
                GERMAN -> ENGLISH
                ENGLISH -> GERMAN
            }

    /**
     * Provide this app language, useful within a [CompositionLocalProvider] call.
     *
     * For example:
     *
     * ```
     * CompositionLocalProvider(AppLanguage.provide()) {
     *   // AppLanguage.current will return "AppLanguage" within this block
     * }
     * ```
     */
    fun provide() = LocalAppLanguage provides mutableStateOf(this)
}

private val languageKey = AppLanguage.entries.createStorageKey("web:appLanguage")

fun AppLanguage.Companion.loadFromLocalStorageOrDefault(): AppLanguage {
    val browserDefault = getPlatformDefaultLibresLocale()
    return loadFromLocalStorage() ?: AppLanguage.entries.find { it.libresLocale.language == browserDefault?.language }
        ?: AppLanguage.ENGLISH
}

fun AppLanguage.Companion.loadFromLocalStorage(): AppLanguage? = window.localStorage.getItem(languageKey)

fun AppLanguage.saveToLocalStorage() {
    window.localStorage.setItem(languageKey, this)
}

@Composable
fun ProvideAppLanguage(content: @Composable () -> Unit) {
    CompositionLocalProvider(
        (AppLanguage.loadFromLocalStorageOrDefault()).provide(),
    ) {
        val language = AppLanguage.current

        // We need to render after we set Libres-Local.
        // Using a separate state field, we can defer rendering until after.
        var localLanguage by remember { mutableStateOf(language) }

        LaunchedEffect(language) {
            language.saveToLocalStorage()

            LibresSettings.setLocaleProvider {
                language.libresLocale
            }

            localLanguage = language
        }

        key(localLanguage) {
            content()
        }
    }
}
